<template>
  <div>
    <div class="add-user-header-div">
      <div class="add-user-title">
        <div class="action-provider-settings-title"></div>
      </div>
      <div class="inline-flex gap-4">
        <RfButtonNew
          type="secondary"
          size="x-small"
          :disabled="userStore.isDisabledWithMessage"
          @click="showAddNewTriggerDialog"
        >
          <RfAddIcon class="h-5! w-5!" />
          Add New Trigger
        </RfButtonNew>
        <RfButtonNew
          type="secondary"
          size="x-small"
          :disabled="userStore.isDisabledWithMessage"
          @click.stop="() => domainChooseDialog?.open()"
        >
          <RfBoltIcon class="h-5! w-5!" />
          Add New Live Trigger
        </RfButtonNew>
      </div>
    </div>
    <RfEditTrigger
      v-if="addNewTriggerDialog"
      :key="editTriggerKey"
      :editTrigger="editingTrigger"
      :creating="creating"
      :app="currApp"
      :width="960"
      @closeNewTriggerDialog="closeNewTriggerDialog"
    />
    <RfDialog
      ref="deleteDialog"
      title="Delete Trigger"
      confirmText="Delete"
      width="500"
      keepOpenOnConfirm
      :loading="deleting"
      @confirm="submitDelete"
      @close="onDeleteClose"
    >
      <div>
        Are you sure you want to delete? This cannot be undone.
        <span v-if="deleteTriggerItems.length"> The following prompts would be affected: </span>
        <ul class="mt-1 list-inside list-disc pl-2">
          <li v-for="item in deleteTriggerItems" :key="item.id">
            <router-link :to="getPromptLink(item.id, $route.params.aid)">{{
              item.name
            }}</router-link>
          </li>
        </ul>
      </div>
    </RfDialog>
    <RfTable
      :tableData="{ totalCount: triggers.length }"
      type="triggers"
      hideTotal
      noIntersection
      noDataText=" You don't have any triggers yet. Add a new trigger."
      class="w-full [&_.rf-table--wrapper]:rounded-none [&_table]:rounded-none"
    >
      <template #thead>
        <tr>
          <th class="tbl-col-tr">Name</th>
          <th class="tbl-col-tr">Type</th>
          <th class="tbl-col-4">Prompts</th>
          <th class="tbl-col-tr">Details</th>
          <template v-if="currApp?.flags?.scheduled_push">
            <th class="tbl-col-tr">Event name</th>
            <th class="tbl-col-tr">Event attributes</th>
          </template>
          <th class="tbl-col-4"></th>
        </tr>
      </template>
      <template #tbody>
        <RfTriggerSettingsRow
          v-for="trigger in triggers"
          :key="trigger.id"
          :trigger="trigger"
          :isPushEnabled="currApp?.flags?.scheduled_push"
          :customTriggers="customTriggers"
          :retentions="prompts"
          @edit="editTrigger(trigger)"
          @remove="openDeleteDialog(trigger.id)"
        />
      </template>
    </RfTable>
    <RfDomainChooserWrapper ref="domainChooseDialog" @open="openLivePreview" />
  </div>
</template>
<script setup>
import RfDomainChooserWrapper from "@/components/RfCommonCards/RfDomainChooserWrapper.vue";
import RfDialog from "@/components/modals/RfDialog.vue";
import RfTable from "@/blocks/RfTable/RfTable.vue";
import { useLive } from "@/utils/composables/useLive";
import RfEditTrigger from "@/components/RfCommonCards/RfEditTrigger.vue";
import { cloneDeep } from "lodash-es";
import { useToastsStore } from "@/pinia/toastsStore";
import { getPromptLink } from "@/utils/getLink";
import { useLegacyLastErrorStore } from "@/pinia/legacyLastErrorStore";
import { useActionsStore } from "@/pinia/actionsStore";
import { useTriggersStore } from "@/pinia/triggersStore";
import { useAllPromptsStore } from "@/pinia/allPromptsStore";
import { useUserStore } from "@/pinia/userStore";
import { computed, ref, watch, onMounted, useTemplateRef } from "vue";
import RfTriggerSettingsRow from "./RfTriggerSettingsRow.vue";
import RfAddIcon from "@/components/icons/RfAddIcon.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfBoltIcon from "@/components/icons/RfBoltIcon.vue";

const props = defineProps({ currApp: { type: Object, required: true } });

const toastsStore = useToastsStore();
const actionsStore = useActionsStore();
const triggersStore = useTriggersStore();
const userStore = useUserStore();
const allPromptsStore = useAllPromptsStore();
const legacyLastErrorStore = useLegacyLastErrorStore();
const live = useLive();
const domainChooseDialog = useTemplateRef("domainChooseDialog");

const prompts = computed(() => allPromptsStore.prompts);

const addNewTriggerDialog = ref(false);
const deleteDialog = useTemplateRef("deleteDialog");
const deleting = ref(false);
const deleteId = ref(null);
const creating = ref(false);
const editingTrigger = ref(null);
const editTriggerKey = ref(0);

const triggers = computed(() => triggersStore.triggers);

const clientActions = computed(() => actionsStore.clientActions || []);

const customTriggers = computed(() => {
  if (props.currApp) {
    const blank = [{ text: "None", value: "" }];
    const actions = clientActions.value.filter(action => action.action_type === "custom_trigger");
    return blank.concat(actions);
  }
  return [];
});

const deleteTriggerItems = computed(() => {
  if (deleteId.value) {
    return prompts.value.filter(
      retention => !!retention.triggers.find(trigger => trigger.id === deleteId.value),
    );
  }
  return [];
});

watch(
  () => legacyLastErrorStore.lastError,
  to => to && toastsStore.create({ type: "error", body: to.message }),
);

onMounted(() => {
  triggersStore.getTriggers(props.currApp.id);
  if (!prompts.value.length) allPromptsStore.getPrompts(props.currApp.id);
  if (!clientActions.value.length) actionsStore.getClientActions(props.currApp.id);
});

const showAddNewTriggerDialog = () => {
  editingTrigger.value = null;
  editTriggerKey.value++;
  creating.value = true;
  addNewTriggerDialog.value = true;
};

const submitDelete = () => {
  if (!deleteId.value) return;
  deleting.value = true;
  triggersStore
    .deleteTrigger({ appId: props.currApp.id, triggerId: deleteId.value })
    .then(() => {
      toastsStore.create({ type: "success", body: "Trigger Deleted" });
      deleteDialog.value?.close();
    })
    .catch(() => null)
    .finally(() => {
      deleting.value = false;
    });
};

const editTrigger = trigger => {
  editingTrigger.value = cloneDeep(trigger);
  editTriggerKey.value++;
  creating.value = false;
  addNewTriggerDialog.value = true;
};

const openDeleteDialog = triggerId => {
  deleteId.value = triggerId;
  deleteDialog.value?.open();
};

const onDeleteClose = () => {
  deleteId.value = null;
};

const closeNewTriggerDialog = () => {
  addNewTriggerDialog.value = false;
};

const openLivePreview = domain => {
  live.openLiveUrl(null, domain, { focus: "triggers" });
  domainChooseDialog.value?.close();
};
</script>
<style scoped>
.config-trigger-specific-pg {
  margin: 6px 0 0 2rem;
}
</style>
